import { Button } from '@progress/kendo-react-buttons';
import { Input } from '@progress/kendo-react-inputs';
import { ic_close_small_black as BlackCloseIcon } from 'app/widgets/SVGs';
import SearchIcon from 'assets/images/ic_search.svg';
import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NoSharedEquipments from '../../../../../assets/images/ill_empty_access_sharing_equipment.png';
import SharedEquipmentRow from './SharedEquipmentRow';
import { useNavigate } from 'react-router-dom';
import { DeleteDialog } from 'app/components/dialogs';
import { ISharedEquipmentEntry } from 'types/IEquipmentEntry';
import { removeEmojis } from 'utils/general';
import { filterSharedEquipment } from 'utils/searchFilter';
import { SharedItem, SharedItemShareType } from 'services/api/shares/types';
import useGetEquipmentData from 'app/hooks/useGetEquipmentData';
import { useNotificationsContext } from 'context';
import { ErrorNotification, Success } from 'app/components/Notifications';
import { useGetUserInfoQuery } from 'app/hooks/user-info';
import { EquipmentListContext } from 'app/components/ReactContexts/equipmentListContext';
import { FilterTabs } from 'app/components/FilterTabs';
import { AcessSharingFilters } from 'app/pages/AccessSharingPage';
import _ from 'lodash';
import { invitationsApi } from 'services/api/invitations/queries';

interface ISharedEquipment {
  isMobile: boolean;
  sharedEquipment: SharedItem[];
}

function SharedEquipment(props: ISharedEquipment) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: user } = useGetUserInfoQuery();

  const [searchValue, setSearchValue] = React.useState<string>('');

  const [isExpanded, setIsExpanded] = React.useState(false);
  const [revokeInvitation] = invitationsApi.useRevokeInvitationMutation();
  const { addNotification } = useNotificationsContext();
  const { optimisticallyRemoveEquipments } = useContext(EquipmentListContext);

  const [selectedEquipmentToRevoke, setSelectedEquipmentToRevoke] =
    useState<ISharedEquipmentEntry>();
  const isRevokeOwnAccessDialogOpen = !!selectedEquipmentToRevoke;

  const { equipments } = useGetEquipmentData();

  const [filter, setFilter] = React.useState<AcessSharingFilters>(
    AcessSharingFilters.All,
  );

  const filterOptions = [
    { value: AcessSharingFilters.All, label: t('all') },
    { value: AcessSharingFilters.OwnedByMe, label: t('owned_by_me') },
    { value: AcessSharingFilters.SharedWithMe, label: t('shared_with_me') },
  ];

  const processedEquipments = useMemo(() => {
    if (!equipments || !props.sharedEquipment) {
      return [];
    }

    const mappedEquipments = props.sharedEquipment
      .map(sharedItem => {
        const equipment = equipments.find(
          eq =>
            eq.shareData?.sharedItemDetails.exposedId ===
            sharedItem.sharedItemDetails.exposedId,
        );
        return equipment;
      })
      .filter((equipment): equipment is ISharedEquipmentEntry => !!equipment);

    const equipmentMachines = _.filter(
      mappedEquipments || [],
      e => e.type === 'machine',
    );

    const equipmentAttatchments = _.filter(
      mappedEquipments || [],
      e => e.type !== 'machine',
    );
    const sortedEquipmentMachines = _.orderBy(
      equipmentMachines || [],
      e => (e.nickName || e.model).toLowerCase() || !e.hasTelematics,
      ['asc'],
    );
    const sortedEquipmentAttatchments = _.orderBy(
      equipmentAttatchments || [],
      e => (e.nickName || e.model).toLowerCase(),
      ['asc'],
    );

    const orderedData = [
      ...sortedEquipmentMachines,
      ...sortedEquipmentAttatchments,
    ];

    // Apply search filter using utility function
    return filterSharedEquipment(orderedData, searchValue);
  }, [props.sharedEquipment, equipments, searchValue]);

  const handleRevokeAccess = async () => {
    try {
      const exposedAccessSharingRecordId =
        selectedEquipmentToRevoke?.shareData?.sharedWith.find(
          sharedWith => sharedWith.email === user?.email,
        )?.exposedAccessSharingRecordId;

      if (
        !exposedAccessSharingRecordId ||
        !selectedEquipmentToRevoke?.isSharedIndividually
      )
        return;

      await revokeInvitation([
        {
          shareType: SharedItemShareType.EQUIPMENT,
          exposedAccessSharingRecordId,
        },
      ]);
      optimisticallyRemoveEquipments([selectedEquipmentToRevoke.id]);
      addNotification(
        <Success text={t('access_sharing.snackbars.access-revoked')} />,
      );
    } catch (error) {
      console.error(error);
      addNotification(<ErrorNotification text={'Server error'} />);
    } finally {
      setSelectedEquipmentToRevoke(undefined);
    }
  };

  return (
    <>
      <div className="access-sharing-subcontainer">
        <div className="access-sharing-subcontainer-header">
          {equipments?.length == 0 ? (
            <h3>{t('shared_equipment_title')}</h3>
          ) : (
            <div className="access-sharing-subcontainer-header-content">
              <h3>{t('shared_equipment_title')}</h3>
              <div className="site-search open">
                <span className="search-icon">
                  <img alt="Search" src={SearchIcon}></img>
                </span>
                <div className="search-input">
                  <Input
                    name="equipment_fleets_search"
                    value={searchValue}
                    placeholder={t('shared_equipment_search_placeholder')}
                    onChange={newValue => {
                      setSearchValue(removeEmojis(newValue.value));
                    }}
                    onClick={() => setIsExpanded(!isExpanded)}
                  />
                  {searchValue && (
                    <span
                      className="search-icon-close"
                      onClick={() => {
                        setSearchValue('');
                      }}
                    >
                      <BlackCloseIcon />
                    </span>
                  )}
                </div>
              </div>

              <FilterTabs
                options={filterOptions}
                activeFilter={filter}
                onChange={setFilter}
              />
            </div>
          )}
        </div>
        <div className="access-sharing-subcontainer-body">
          {processedEquipments.length == 0 ? (
            <div className="access-sharing-subcontainer-body-empty">
              <div className="access-sharing-body-picture">
                <img src={NoSharedEquipments}></img>
              </div>
              <div className="access-sharing-body-text">
                <h4>{t('shared_equipment_no_equipment_title')}</h4>
                <p>{t('shared_equipment_no_equipment_text')}</p>
              </div>
            </div>
          ) : (
            <div className="access-sharing-subcontainer-body-table">
              <div className="access-sharing-subcontainer-body-table-header">
                <div>{t('equipment')}</div>
                <div>{t('my_access')}</div>
                <div>{t('owner')}</div>
                <div></div>
                <div>{t('shared_with')}</div>
                <div></div>
              </div>
              <div className="access-sharing-subcontainer-body-table-body">
                {processedEquipments.map(equipment => {
                  return (
                    <SharedEquipmentRow
                      key={equipment.id}
                      equipment={equipment}
                      isMobile={props.isMobile}
                      onRevokeOwnAccess={() =>
                        setSelectedEquipmentToRevoke(equipment)
                      }
                    />
                  );
                })}
              </div>
            </div>
          )}
          <div className="access-sharing-subcontainer-body-buttons-container">
            <Button
              themeColor="secondary"
              onClick={() => navigate('/access/share-equipment')}
            >
              {t('shared_equipment_button').toUpperCase()}
            </Button>
          </div>
        </div>
      </div>

      {isRevokeOwnAccessDialogOpen && (
        <DeleteDialog
          open
          title={t('revoke_access_confirmation', {
            email: user?.email,
            machineName:
              selectedEquipmentToRevoke?.nickName ||
              selectedEquipmentToRevoke?.model,
          })}
          confirmationLabel={t('access_sharing.role_based_actions.revoke')}
          onClose={() => setSelectedEquipmentToRevoke(undefined)}
          onConfirm={handleRevokeAccess}
        />
      )}
    </>
  );
}

export default SharedEquipment;
